import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import {
  LayoutDashboard, Package, Puzzle, CalendarDays, Users, MessageSquare,
  LogOut, Plus, Pencil, Trash2, X, Check, Loader2, DollarSign,
  ClipboardList, TrendingUp, RefreshCw, Settings, Shield, UserPlus,
  Eye, EyeOff, Key, FileText
} from 'lucide-react';
import {
  getAdminDashboard, getAdminPackages, getAdminModules, getAdminCategories,
  getAdminPlans, getAdminOrders, getAdminLeads, createPackage, updatePackage,
  deletePackage, createModule, updateModule, deleteModule, updateOrderStatus,
  updateLeadStatus, changePassword, createAdmin, listAdmins, deleteAdmin,
  createPlan, updatePlan, deletePlan, getAdminRegionalPricing,
  updatePricingRegion, updateRegionalPrice, publishRegionalPricing
} from '../services/api';
import ClientDemosManager from '../components/admin/ClientDemosManager';
import ContentManager from '../components/admin/ContentManager';

const AdminDashboard = () => {
  const { admin, logout, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(false);
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [packages, setPackages] = useState([]);
  const [modules, setModules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [plans, setPlans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [adminList, setAdminList] = useState([]);
  const [regionalPricing, setRegionalPricing] = useState({ regions: [], prices: [] });
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editItem, setEditItem] = useState(null);

  const isPrimaryAdmin = Boolean(admin?.is_super_admin);

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin');
    }
  }, [admin, authLoading, navigate]);

  const loadTabData = useCallback(async (tab) => {
    setIsLoading(true);
    try {
      switch (tab) {
        case 'dashboard':
          const dashData = await getAdminDashboard();
          setDashboard(dashData);
          break;
        case 'packages':
          const pkgData = await getAdminPackages();
          setPackages(pkgData);
          break;
        case 'modules':
          const modData = await getAdminModules();
          const catData = await getAdminCategories();
          setModules(modData);
          setCategories(catData);
          break;
        case 'plans':
          const planData = await getAdminPlans();
          setPlans(planData);
          break;
        case 'regional-pricing':
          const regionalData = await getAdminRegionalPricing();
          setRegionalPricing(regionalData);
          break;
        case 'orders':
          const orderData = await getAdminOrders();
          setOrders(orderData);
          break;
        case 'leads':
          const leadData = await getAdminLeads();
          setLeads(leadData);
          break;
        case 'client-demos':
          break;
        case 'content':
          break;
        case 'admins':
          if (isPrimaryAdmin) {
            const adminsData = await listAdmins();
            setAdminList(adminsData);
          }
          break;
        case 'settings':
          // No data to load for settings
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isPrimaryAdmin]);

  useEffect(() => {
    if (admin) {
      loadTabData(activeTab);
    }
  }, [activeTab, admin, loadTabData]);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setEditItem(null);
  };

  // Base tabs for all admins
  const baseTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'packages', label: 'Packages', icon: Package },
    { id: 'modules', label: 'Modules', icon: Puzzle },
    { id: 'plans', label: 'Plans', icon: CalendarDays },
    { id: 'regional-pricing', label: 'Regional Pricing', icon: DollarSign },
    { id: 'orders', label: 'Requests', icon: ClipboardList },
    { id: 'leads', label: 'Leads', icon: MessageSquare },
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'client-demos', label: 'Client Demos', icon: Key },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // Add admin management tab only for the backend-authorized super admin.
  const tabs = isPrimaryAdmin 
    ? [...baseTabs, { id: 'admins', label: 'Admin Users', icon: Shield }]
    : baseTabs;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00FFD1] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Sidebar */}
      <div 
        className="fixed left-0 top-0 bottom-0 w-64 p-4"
        style={{
          background: 'rgba(18, 18, 18, 0.95)',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8 px-2">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.2) 0%, rgba(0, 200, 165, 0.3) 100%)',
              border: '1px solid rgba(0, 255, 209, 0.3)',
            }}
          >
            <span className="text-[#00FFD1] font-bold text-lg">W</span>
          </div>
          <div>
            <h2 className="text-white font-semibold">Webouse</h2>
            <p className="text-white/50 text-xs">Admin Panel</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="space-y-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`nav-${tab.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === tab.id 
                  ? 'bg-[#00FFD1]/10 text-[#00FFD1]' 
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="absolute bottom-4 left-4 right-4">
          <div 
            className="p-4 rounded-xl mb-3"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <div className="flex items-center gap-2">
              <p className="text-white font-medium text-sm">{admin?.username}</p>
              {isPrimaryAdmin && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[#00FFD1]/20 text-[#00FFD1] rounded">
                  Primary
                </span>
              )}
            </div>
            <p className="text-white/50 text-xs capitalize">{admin?.role}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-red-400 hover:bg-red-400/10 transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white capitalize">{activeTab}</h1>
            <p className="text-white/50 text-sm">
              {activeTab === 'settings' ? 'Manage your account settings' : 
               activeTab === 'admins' ? 'Manage admin users' : 
               activeTab === 'content' ? 'Manage service, resource and case-study records' :
               activeTab === 'client-demos' ? 'Manage private client demo access' :
               `Manage your ${activeTab}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {['packages', 'modules', 'plans'].includes(activeTab) && (
              <button
                onClick={() => openModal(
                  activeTab === 'packages'
                    ? 'package'
                    : activeTab === 'modules'
                      ? 'module'
                      : 'plan'
                )}
                data-testid={`add-${activeTab.slice(0, -1)}-button`}
                className="btn-glass-primary"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.95) 0%, rgba(0, 200, 165, 1) 100%)',
                  color: '#000',
                  borderRadius: '12px',
                  padding: '10px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Plus className="w-5 h-5" />
                Add {
                  activeTab === 'packages'
                    ? 'Package'
                    : activeTab === 'modules'
                      ? 'Module'
                      : 'Plan'
                }
              </button>
            )}
            {activeTab === 'admins' && isPrimaryAdmin && (
              <button
                onClick={() => openModal('new-admin')}
                data-testid="add-admin-button"
                className="btn-glass-primary"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.95) 0%, rgba(0, 200, 165, 1) 100%)',
                  color: '#000',
                  borderRadius: '12px',
                  padding: '10px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <UserPlus className="w-5 h-5" />
                Add Admin
              </button>
            )}
            {activeTab !== 'settings' && (
              <button
                onClick={() => loadTabData(activeTab)}
                className="glass-icon-btn"
                style={{
                  width: '44px',
                  height: '44px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                }}
                data-testid="refresh-button"
              >
                <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
        </div>

        {/* Content Area */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#00FFD1] animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && <DashboardView data={dashboard} />}
            {activeTab === 'packages' && (
              <PackagesView 
                packages={packages} 
                onEdit={(pkg) => openModal('package', pkg)}
                onDelete={async (id) => {
                  await deletePackage(id);
                  loadTabData('packages');
                }}
              />
            )}
            {activeTab === 'modules' && (
              <ModulesView 
                modules={modules}
                categories={categories}
                onEdit={(mod) => openModal('module', mod)}
                onDelete={async (id) => {
                  await deleteModule(id);
                  loadTabData('modules');
                }}
              />
            )}
            {activeTab === 'plans' && (
              <PlansView
                plans={plans}
                onEdit={(plan) => openModal('plan', plan)}
                onDelete={async (id) => {
                  await deletePlan(id);
                  loadTabData('plans');
                }}
              />
            )}
            {activeTab === 'regional-pricing' && (
              <RegionalPricingView
                data={regionalPricing}
                onReload={() => loadTabData('regional-pricing')}
              />
            )}
            {activeTab === 'orders' && (
              <OrdersView 
                orders={orders}
                onStatusUpdate={async (id, status) => {
                  await updateOrderStatus(id, status);
                  loadTabData('orders');
                }}
              />
            )}
            {activeTab === 'leads' && (
              <LeadsView 
                leads={leads}
                onStatusUpdate={async (id, status) => {
                  await updateLeadStatus(id, status);
                  loadTabData('leads');
                }}
              />
            )}
            {activeTab === 'client-demos' && <ClientDemosManager />}
            {activeTab === 'content' && <ContentManager />}
            {activeTab === 'settings' && <SettingsView />}
            {activeTab === 'admins' && isPrimaryAdmin && (
              <AdminsView 
                admins={adminList}
                currentAdminId={admin?.id}
                onDelete={async (id) => {
                  await deleteAdmin(id);
                  loadTabData('admins');
                }}
              />
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <Modal
          type={modalType}
          item={editItem}
          categories={categories}
          onClose={closeModal}
          onSave={async () => {
            await loadTabData(activeTab);
            closeModal();
          }}
        />
      )}
    </div>
  );
};

// Dashboard View Component
const DashboardView = ({ data }) => {
  if (!data) return null;

  const stats = [
    { label: 'Total Requests', value: data.orders.total, icon: ClipboardList, color: '#00FFD1' },
    { label: 'Pending Requests', value: data.orders.pending, icon: Package, color: '#FFB800' },
    { label: 'Pipeline Value', value: `$${data.pipeline.one_time.toFixed(2)}`, icon: DollarSign, color: '#00FFD1' },
    { label: 'Total Leads', value: data.leads.total, icon: Users, color: '#8B5CF6' },
    { label: 'New Leads', value: data.leads.new, icon: TrendingUp, color: '#10B981' },
    { label: 'Active Packages', value: data.content.packages, icon: Package, color: '#3B82F6' },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="dashboard-stats">
      {stats.map((stat, index) => (
        <div
          key={index}
          className="glass-card p-6"
          style={{
            background: 'rgba(18, 18, 18, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: `${stat.color}15` }}
            >
              <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
            </div>
          </div>
          <p className="text-white/50 text-sm mb-1">{stat.label}</p>
          <p className="text-2xl font-bold text-white">{stat.value}</p>
        </div>
      ))}
    </div>
  );
};

// Packages View Component
const PackagesView = ({ packages, onEdit, onDelete }) => {
  return (
    <div className="grid gap-4" data-testid="packages-list">
      {packages.map((pkg) => (
        <div
          key={pkg.id}
          className="glass-card p-6 flex items-center justify-between"
          style={{
            background: 'rgba(18, 18, 18, 0.8)',
            border: pkg.is_custom ? '1px solid rgba(0, 255, 209, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
          }}
          data-testid={`package-item-${pkg.id}`}
        >
          <div className="flex items-center gap-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ 
                background: pkg.is_custom ? 'rgba(0, 255, 209, 0.15)' : 'rgba(255, 255, 255, 0.05)',
              }}
            >
              <Package className={`w-6 h-6 ${pkg.is_custom ? 'text-[#00FFD1]' : 'text-white/60'}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-white font-semibold">{pkg.name}</h3>
                {pkg.is_popular && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-[#00FFD1] text-black rounded">
                    Popular
                  </span>
                )}
                {pkg.is_custom && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-[#00FFD1]/20 text-[#00FFD1] rounded">
                    Custom
                  </span>
                )}
              </div>
              <p className="text-white/50 text-sm">
                {pkg.features?.length || 0} features • {pkg.is_active ? 'Active' : 'Inactive'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xl font-bold text-white">
              {pkg.price ? `$${pkg.price}` : 'Custom'}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onEdit(pkg)}
                className="glass-icon-btn"
                data-testid={`edit-package-${pkg.id}`}
                style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                }}
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(pkg.id)}
                className="glass-icon-btn"
                data-testid={`delete-package-${pkg.id}`}
                style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(255, 59, 48, 0.1)',
                  border: '1px solid rgba(255, 59, 48, 0.2)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ff6b6b',
                  cursor: 'pointer',
                }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Modules View Component
const ModulesView = ({ modules, categories, onEdit, onDelete }) => {
  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat?.name || 'Unknown';
  };

  return (
    <div className="grid gap-4" data-testid="modules-list">
      {modules.map((mod) => (
        <div
          key={mod.id}
          className="glass-card p-6 flex items-center justify-between"
          style={{
            background: 'rgba(18, 18, 18, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
          }}
          data-testid={`module-item-${mod.id}`}
        >
          <div className="flex items-center gap-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(0, 255, 209, 0.1)' }}
            >
              <Puzzle className="w-6 h-6 text-[#00FFD1]" />
            </div>
            <div>
              <h3 className="text-white font-semibold">{mod.name}</h3>
              <p className="text-white/50 text-sm">{getCategoryName(mod.category_id)}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xl font-bold text-[#00FFD1]">${mod.price}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onEdit(mod)}
                className="glass-icon-btn"
                data-testid={`edit-module-${mod.id}`}
                style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                }}
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(mod.id)}
                className="glass-icon-btn"
                data-testid={`delete-module-${mod.id}`}
                style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(255, 59, 48, 0.1)',
                  border: '1px solid rgba(255, 59, 48, 0.2)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ff6b6b',
                  cursor: 'pointer',
                }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Plans View Component
const PlansView = ({ plans, onEdit, onDelete }) => {
  return (
    <div className="grid gap-4" data-testid="plans-list">
      {plans.map((plan) => (
        <div
          key={plan.id}
          className="glass-card p-6 flex items-center justify-between"
          style={{
            background: plan.is_popular ? 'rgba(0, 255, 209, 0.05)' : 'rgba(18, 18, 18, 0.8)',
            border: plan.is_popular ? '1px solid rgba(0, 255, 209, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
          }}
        >
          <div className="flex items-center gap-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: plan.is_popular ? 'rgba(0, 255, 209, 0.15)' : 'rgba(255, 255, 255, 0.05)' }}
            >
              <CalendarDays className={`w-6 h-6 ${plan.is_popular ? 'text-[#00FFD1]' : 'text-white/60'}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-white font-semibold">{plan.name}</h3>
                {plan.is_popular && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-[#00FFD1] text-black rounded">
                    Recommended
                  </span>
                )}
              </div>
              <p className="text-white/50 text-sm">{plan.features?.length || 0} features</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xl font-bold text-white">${plan.price}/mo</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onEdit(plan)}
                className="glass-icon-btn"
                data-testid={`edit-plan-${plan.id}`}
                style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                }}
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(plan.id)}
                className="glass-icon-btn"
                data-testid={`delete-plan-${plan.id}`}
                style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(255, 59, 48, 0.1)',
                  border: '1px solid rgba(255, 59, 48, 0.2)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ff6b6b',
                  cursor: 'pointer',
                }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Regional Pricing View Component
const RegionalPricingView = ({ data, onReload }) => {
  const regions = useMemo(() => data?.regions || [], [data]);
  const prices = useMemo(() => data?.prices || [], [data]);
  const [selectedRegionId, setSelectedRegionId] = useState(regions[0]?.id || 'US_GLOBAL');
  const [draftAmounts, setDraftAmounts] = useState({});
  const [message, setMessage] = useState('');
  const selectedRegion =
    regions.find((region) => region.id === selectedRegionId) ||
    regions[0] ||
    {};

  useEffect(() => {
    if (!regions.length) return;
    if (!regions.some((region) => region.id === selectedRegionId)) {
      setSelectedRegionId(regions[0].id);
    }
  }, [regions, selectedRegionId]);

  useEffect(() => {
    setDraftAmounts(
      prices.reduce((acc, price) => ({
        ...acc,
        [price.id]: price.amount ?? 0,
      }), {})
    );
  }, [prices]);

  const formatAdminPrice = (amount, region) => {
    if (!region?.currency_code) return amount;
    return new Intl.NumberFormat(region.locale || 'en-US', {
      style: 'currency',
      currency: region.currency_code,
      currencyDisplay: ['KWD', 'AED'].includes(region.currency_code)
        ? 'code'
        : 'narrowSymbol',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(Number(amount || 0));
  };

  const showMessage = (text) => {
    setMessage(text);
    window.setTimeout(() => setMessage(''), 2500);
  };

  const handleRegionUpdate = async (regionId, patch) => {
    await updatePricingRegion(regionId, patch);
    showMessage('Region saved');
    onReload();
  };

  const handleSavePrice = async (price) => {
    await updateRegionalPrice(price.id, {
      amount: Number(draftAmounts[price.id] ?? price.amount ?? 0),
      is_active: price.is_active !== false,
    });
    showMessage('Price saved as draft');
    onReload();
  };

  const handlePublish = async () => {
    await publishRegionalPricing();
    showMessage('Regional pricing published');
    onReload();
  };

  const selectedPrices = prices
    .filter((price) => price.region_id === selectedRegion.id)
    .sort((a, b) => `${a.item_type}-${a.item_name}`.localeCompare(`${b.item_type}-${b.item_name}`));

  return (
    <div className="space-y-6" data-testid="regional-pricing-view">
      <div
        className="glass-card p-6 flex items-center justify-between gap-4"
        style={{
          background: 'rgba(18, 18, 18, 0.8)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
        }}
      >
        <div>
          <h3 className="text-white text-xl font-semibold">Regional Pricing</h3>
          <p className="text-white/50 text-sm">
            Fixed country prices. These are not live exchange-rate conversions.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {message && <span className="text-[#00FFD1] text-sm">{message}</span>}
          <button
            onClick={handlePublish}
            className="btn-glass-primary"
            style={{
              color: '#000',
              borderRadius: '12px',
              padding: '10px 18px',
              fontWeight: 700,
            }}
          >
            Publish
          </button>
        </div>
      </div>

      <div className="grid xl:grid-cols-[320px_minmax(0,1fr)] gap-6">
        <div className="space-y-3">
          {regions.map((region) => (
            <button
              key={region.id}
              onClick={() => setSelectedRegionId(region.id)}
              className="w-full text-left glass-card p-4"
              style={{
                background:
                  selectedRegion.id === region.id
                    ? 'rgba(0, 255, 209, 0.08)'
                    : 'rgba(18, 18, 18, 0.8)',
                border:
                  selectedRegion.id === region.id
                    ? '1px solid rgba(0, 255, 209, 0.35)'
                    : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-white font-semibold">{region.label}</p>
                  <p className="text-white/45 text-xs">
                    {region.id} / {region.currency_code}
                  </p>
                </div>
                <span className={`text-xs ${region.is_active ? 'text-[#00FFD1]' : 'text-red-300'}`}>
                  {region.is_active ? 'Active' : 'Off'}
                </span>
              </div>
            </button>
          ))}
        </div>

        <div
          className="glass-card p-6"
          style={{
            background: 'rgba(18, 18, 18, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
          }}
        >
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <h3 className="text-white text-xl font-semibold">
                {selectedRegion.label || selectedRegion.id}
              </h3>
              <p className="text-white/45 text-sm">
                {selectedRegion.currency_code} / {selectedRegion.locale}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() =>
                  handleRegionUpdate(selectedRegion.id, {
                    is_active: !selectedRegion.is_active,
                  })
                }
                className="px-3 py-2 rounded-lg bg-white/10 text-white/70 hover:text-white text-sm"
              >
                {selectedRegion.is_active ? 'Disable' : 'Enable'}
              </button>
              <button
                onClick={() =>
                  handleRegionUpdate(selectedRegion.id, { is_default: true })
                }
                className="px-3 py-2 rounded-lg bg-white/10 text-white/70 hover:text-white text-sm"
              >
                {selectedRegion.is_default ? 'Default' : 'Set Default'}
              </button>
            </div>
          </div>

          <label className="block text-white/60 text-sm mb-2">
            Country mappings
          </label>
          <input
            defaultValue={(selectedRegion.countries || []).join(', ')}
            onBlur={(event) =>
              handleRegionUpdate(selectedRegion.id, {
                countries: event.target.value
                  .split(',')
                  .map((value) => value.trim().toUpperCase())
                  .filter(Boolean),
              })
            }
            className="w-full mb-6"
            style={{
              background: 'rgba(0, 0, 0, 0.6)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '12px',
              color: '#fff',
              padding: '12px 14px',
            }}
          />

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/45 border-b border-white/10">
                  <th className="text-left py-3 pe-4">Item</th>
                  <th className="text-left py-3 pe-4">Type</th>
                  <th className="text-left py-3 pe-4">Period</th>
                  <th className="text-left py-3 pe-4">Amount</th>
                  <th className="text-left py-3 pe-4">Preview</th>
                  <th className="text-right py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {selectedPrices.map((price) => (
                  <tr key={price.id} className="border-b border-white/5">
                    <td className="py-3 pe-4 text-white">{price.item_name}</td>
                    <td className="py-3 pe-4 text-white/55">{price.item_type}</td>
                    <td className="py-3 pe-4 text-white/55">{price.billing_period}</td>
                    <td className="py-3 pe-4">
                      <input
                        type="number"
                        min="0"
                        value={draftAmounts[price.id] ?? ''}
                        onChange={(event) =>
                          setDraftAmounts((current) => ({
                            ...current,
                            [price.id]: event.target.value,
                          }))
                        }
                        className="w-28"
                        style={{
                          background: 'rgba(0, 0, 0, 0.6)',
                          border: '1px solid rgba(255, 255, 255, 0.12)',
                          borderRadius: '10px',
                          color: '#fff',
                          padding: '9px 10px',
                        }}
                      />
                    </td>
                    <td className="py-3 pe-4 text-[#00FFD1]">
                      {formatAdminPrice(draftAmounts[price.id] ?? price.amount, selectedRegion)}
                    </td>
                    <td className="py-3 text-right">
                      <button
                        onClick={() => handleSavePrice(price)}
                        className="px-3 py-2 rounded-lg bg-[#00FFD1]/15 text-[#00FFD1] hover:bg-[#00FFD1]/25 text-sm"
                      >
                        Save
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Project Requests View Component
const OrdersView = ({ orders, onStatusUpdate }) => {
  const formatOrderValue = (order, amount) => {
    if (!order.currency_code) return `$${amount}`;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: order.currency_code,
      currencyDisplay: ['KWD', 'AED'].includes(order.currency_code)
        ? 'code'
        : 'narrowSymbol',
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#FFB800';
      case 'contacted': return '#3B82F6';
      case 'proposal_sent': return '#8B5CF6';
      case 'approved': return '#10B981';
      case 'in_progress': return '#06B6D4';
      case 'completed': return '#00FFD1';
      case 'cancelled': return '#EF4444';
      default: return '#6B7280';
    }
  };

  return (
    <div className="space-y-4" data-testid="orders-list">
      {orders.length === 0 ? (
        <div className="text-center py-12 text-white/50">No project requests yet</div>
      ) : (
        orders.map((order) => (
          <div
            key={order.id}
            className="glass-card p-6"
            style={{
              background: 'rgba(18, 18, 18, 0.8)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
            }}
            data-testid={`order-item-${order.id}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-white font-semibold">{order.customer_name}</h3>
                  <span 
                    className="px-2 py-1 text-xs font-medium rounded capitalize"
                    style={{ 
                      background: `${getStatusColor(order.status)}20`,
                      color: getStatusColor(order.status),
                    }}
                  >
                    {order.status}
                  </span>
                </div>
                <p className="text-white/50 text-sm">{order.customer_email}</p>
                <p className="text-white/40 text-xs mt-1">
                  {order.package_name || 'Custom Package'} • {order.plan_name || 'No plan'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-white">{formatOrderValue(order, order.one_time_total)}</p>
                {order.monthly_total > 0 && (
                  <p className="text-white/50 text-sm">+{formatOrderValue(order, order.monthly_total)}/mo</p>
                )}
                <select
                  value={order.status}
                  onChange={(e) => onStatusUpdate(order.id, e.target.value)}
                  className="mt-2 text-xs px-2 py-1 rounded bg-white/10 border border-white/20 text-white"
                  data-testid={`order-status-select-${order.id}`}
                >
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="proposal_sent">Proposal Sent</option>
                  <option value="approved">Approved</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// Leads View Component
const LeadsView = ({ leads, onStatusUpdate }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return '#00FFD1';
      case 'contacted': return '#3B82F6';
      case 'converted': return '#10B981';
      case 'closed': return '#6B7280';
      default: return '#6B7280';
    }
  };

  return (
    <div className="space-y-4" data-testid="leads-list">
      {leads.length === 0 ? (
        <div className="text-center py-12 text-white/50">No leads yet</div>
      ) : (
        leads.map((lead) => (
          <div
            key={lead.id}
            className="glass-card p-6"
            style={{
              background: 'rgba(18, 18, 18, 0.8)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
            }}
            data-testid={`lead-item-${lead.id}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-white font-semibold">{lead.name}</h3>
                  <span 
                    className="px-2 py-1 text-xs font-medium rounded capitalize"
                    style={{ 
                      background: `${getStatusColor(lead.status)}20`,
                      color: getStatusColor(lead.status),
                    }}
                  >
                    {lead.status}
                  </span>
                </div>
                <p className="text-white/50 text-sm">{lead.email}</p>
                <p className="text-white/40 text-xs">{lead.business}</p>
                {lead.message && (
                  <p className="text-white/60 text-sm mt-2 max-w-lg">{lead.message}</p>
                )}
              </div>
              <select
                value={lead.status}
                onChange={(e) => onStatusUpdate(lead.id, e.target.value)}
                className="text-xs px-2 py-1 rounded bg-white/10 border border-white/20 text-white"
                data-testid={`lead-status-select-${lead.id}`}
              >
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="converted">Converted</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// Settings View Component - Change Password
const SettingsView = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    setIsLoading(true);
    try {
      await changePassword(currentPassword, newPassword, confirmPassword);
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      setMessage({ type: 'error', text: errorMsg });
    }
    setIsLoading(false);
  };

  return (
    <div className="max-w-xl" data-testid="settings-view">
      {/* Change Password Card */}
      <div
        className="glass-card p-8"
        style={{
          background: 'rgba(18, 18, 18, 0.8)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
        }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(0, 255, 209, 0.1)' }}
          >
            <Key className="w-6 h-6 text-[#00FFD1]" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-white">Change Password</h3>
            <p className="text-white/50 text-sm">Update your account password</p>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div 
            className="mb-6 p-4 rounded-xl text-sm"
            style={{
              background: message.type === 'error' ? 'rgba(255, 59, 48, 0.1)' : 'rgba(0, 255, 209, 0.1)',
              border: `1px solid ${message.type === 'error' ? 'rgba(255, 59, 48, 0.3)' : 'rgba(0, 255, 209, 0.3)'}`,
              color: message.type === 'error' ? '#ff6b6b' : '#00FFD1',
            }}
            data-testid="password-message"
          >
            {message.text}
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-5">
          {/* Current Password */}
          <div>
            <label className="block text-white/70 text-sm font-medium mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                data-testid="current-password-input"
                className="w-full"
                style={{
                  background: 'rgba(0, 0, 0, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '12px',
                  color: '#fff',
                  padding: '14px 48px 14px 16px',
                }}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
              >
                {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-white/70 text-sm font-medium mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
                className="w-full"
                style={{
                  background: 'rgba(0, 0, 0, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '12px',
                  color: '#fff',
                  padding: '14px 48px 14px 16px',
                }}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-white/40 text-xs mt-1">Minimum 6 characters</p>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-white/70 text-sm font-medium mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              data-testid="confirm-password-input"
              className="w-full"
              style={{
                background: 'rgba(0, 0, 0, 0.6)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '12px',
                color: '#fff',
                padding: '14px 16px',
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            data-testid="change-password-button"
            className="w-full"
            style={{
              background: isLoading 
                ? 'rgba(0, 255, 209, 0.5)' 
                : 'linear-gradient(135deg, rgba(0, 255, 209, 0.95) 0%, rgba(0, 200, 165, 1) 100%)',
              color: '#000',
              borderRadius: '12px',
              padding: '14px 24px',
              fontWeight: '600',
              fontSize: '16px',
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Changing...
              </span>
            ) : (
              'Change Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// Admins View Component - Only for a backend-authorized super admin.
const AdminsView = ({ admins, currentAdminId, onDelete }) => {
  return (
    <div className="space-y-4" data-testid="admins-list">
      {admins.length === 0 ? (
        <div className="text-center py-12 text-white/50">No admin users found</div>
      ) : (
        admins.map((adminUser) => (
          <div
            key={adminUser.id}
            className="glass-card p-6 flex items-center justify-between"
            style={{
              background: adminUser.is_super_admin
                ? 'rgba(0, 255, 209, 0.05)' 
                : 'rgba(18, 18, 18, 0.8)',
              border: adminUser.is_super_admin
                ? '1px solid rgba(0, 255, 209, 0.3)' 
                : '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
            }}
            data-testid={`admin-item-${adminUser.id}`}
          >
            <div className="flex items-center gap-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ 
                  background: adminUser.is_super_admin
                    ? 'rgba(0, 255, 209, 0.15)' 
                    : 'rgba(255, 255, 255, 0.05)'
                }}
              >
                <Shield className={`w-6 h-6 ${adminUser.is_super_admin ? 'text-[#00FFD1]' : 'text-white/60'}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-semibold">{adminUser.username}</h3>
                  {adminUser.is_super_admin && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-[#00FFD1] text-black rounded">
                      Primary Admin
                    </span>
                  )}
                </div>
                <p className="text-white/50 text-sm capitalize">
                  {adminUser.role} • {adminUser.is_active ? 'Active' : 'Inactive'}
                </p>
                {adminUser.last_login && (
                  <p className="text-white/40 text-xs">
                    Last login: {new Date(adminUser.last_login).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!adminUser.is_super_admin && adminUser.id !== currentAdminId && (
                <button
                  onClick={() => onDelete(adminUser.id)}
                  className="glass-icon-btn"
                  data-testid={`delete-admin-${adminUser.id}`}
                  style={{
                    width: '40px',
                    height: '40px',
                    background: 'rgba(255, 59, 48, 0.1)',
                    border: '1px solid rgba(255, 59, 48, 0.2)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ff6b6b',
                    cursor: 'pointer',
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// Modal Component
const Modal = ({ type, item, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    price: item?.price ?? '',
    is_popular: item?.is_popular || false,
    is_custom: item?.is_custom || false,
    is_active: item?.is_active ?? true,
    category_id: item?.category_id || categories?.[0]?.id || '',
    featuresText: (item?.features || [])
      .map((feature) => feature.text || feature)
      .join('\n'),
    username: '',
    password: '',
    role: 'admin',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const features = formData.featuresText
        .split('\n')
        .map((text) => text.trim())
        .filter(Boolean)
        .map((text, index) => ({
          text,
          text_translations: {
            ...(item?.features?.[index]?.text_translations || {}),
            en: text,
          },
        }));

      if (type === 'package') {
        const packageData = {
          name: formData.name,
          name_translations: {
            ...(item?.name_translations || {}),
            en: formData.name,
          },
          price: parseFloat(formData.price),
          is_popular: formData.is_popular,
          is_custom: formData.is_custom,
          is_active: formData.is_active,
          features,
        };
        if (item) {
          await updatePackage(item.id, packageData);
        } else {
          await createPackage(packageData);
        }
      } else if (type === 'module') {
        const moduleData = {
          name: formData.name,
          name_translations: {
            ...(item?.name_translations || {}),
            en: formData.name,
          },
          price: parseFloat(formData.price),
          category_id: formData.category_id,
        };
        if (item) {
          await updateModule(item.id, moduleData);
        } else {
          await createModule(moduleData);
        }
      } else if (type === 'plan') {
        const planData = {
          name: formData.name,
          name_translations: {
            ...(item?.name_translations || {}),
            en: formData.name,
          },
          price: parseFloat(formData.price),
          is_popular: formData.is_popular,
          is_active: formData.is_active,
          features,
        };
        if (item) {
          await updatePlan(item.id, planData);
        } else {
          await createPlan(planData);
        }
      } else if (type === 'new-admin') {
        await createAdmin(formData.username, formData.password, formData.role);
      }
      onSave();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'An error occurred';
      setError(errorMsg);
    }
    setIsLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        className="w-full max-w-md"
        style={{
          background: 'rgba(18, 18, 18, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        }}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-semibold text-white">
            {type === 'new-admin' 
              ? 'Add New Admin' 
              : `${item ? 'Edit' : 'Add'} ${
                  type === 'package'
                    ? 'Package'
                    : type === 'module'
                      ? 'Module'
                      : 'Plan'
                }`}
          </h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {error && (
          <div 
            className="mx-6 mt-4 p-3 rounded-xl text-sm"
            style={{
              background: 'rgba(255, 59, 48, 0.1)',
              border: '1px solid rgba(255, 59, 48, 0.3)',
              color: '#ff6b6b',
            }}
          >
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {type === 'new-admin' ? (
            <>
              {/* Admin Username */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  minLength={3}
                  data-testid="modal-admin-username"
                  style={{
                    background: 'rgba(0, 0, 0, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '12px',
                    color: '#fff',
                    padding: '12px 16px',
                    width: '100%',
                  }}
                />
              </div>
              
              {/* Admin Password */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="modal-admin-password"
                  style={{
                    background: 'rgba(0, 0, 0, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '12px',
                    color: '#fff',
                    padding: '12px 16px',
                    width: '100%',
                  }}
                />
                <p className="text-white/40 text-xs mt-1">Minimum 6 characters</p>
              </div>
              
              {/* Admin Role */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  data-testid="modal-admin-role"
                  style={{
                    background: 'rgba(0, 0, 0, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '12px',
                    color: '#fff',
                    padding: '12px 16px',
                    width: '100%',
                  }}
                >
                  <option value="admin">Admin</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="modal-name-input"
                  style={{
                    background: 'rgba(0, 0, 0, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '12px',
                    color: '#fff',
                    padding: '12px 16px',
                    width: '100%',
                  }}
                />
              </div>
              
              {(type === 'package' || type === 'plan') && (
                <div className="flex items-center gap-4">
                  {type === 'package' && (
                    <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_custom}
                        onChange={(e) => setFormData({ ...formData, is_custom: e.target.checked })}
                        className="w-4 h-4 rounded"
                        data-testid="modal-is-custom"
                      />
                      Custom Package
                    </label>
                  )}
                  <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_popular}
                      onChange={(e) => setFormData({ ...formData, is_popular: e.target.checked })}
                      className="w-4 h-4 rounded"
                      data-testid="modal-is-popular"
                    />
                    Popular
                  </label>
                </div>
              )}

              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  {type === 'plan'
                    ? 'Monthly Price ($)'
                    : formData.is_custom
                      ? 'Base Price ($)'
                      : 'Price ($)'}
                </label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                  data-testid="modal-price-input"
                  style={{
                    background: 'rgba(0, 0, 0, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '12px',
                    color: '#fff',
                    padding: '12px 16px',
                    width: '100%',
                  }}
                />
              </div>

              {(type === 'package' || type === 'plan') && (
                <div>
                  <label className="block text-white/70 text-sm font-medium mb-2">
                    Features (one per line)
                  </label>
                  <textarea
                    value={formData.featuresText}
                    onChange={(e) => setFormData({ ...formData, featuresText: e.target.value })}
                    rows={5}
                    data-testid="modal-features-input"
                    style={{
                      background: 'rgba(0, 0, 0, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      borderRadius: '12px',
                      color: '#fff',
                      padding: '12px 16px',
                      width: '100%',
                      resize: 'vertical',
                    }}
                  />
                </div>
              )}

              {(type === 'package' || type === 'plan') && item && (
                <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded"
                    data-testid="modal-is-active"
                  />
                  Active
                </label>
              )}

              {type === 'module' && (
                <div>
                  <label className="block text-white/70 text-sm font-medium mb-2">Category</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    required
                    data-testid="modal-category-select"
                    style={{
                      background: 'rgba(0, 0, 0, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      borderRadius: '12px',
                      color: '#fff',
                      padding: '12px 16px',
                      width: '100%',
                    }}
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1"
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '12px',
                color: '#fff',
                padding: '12px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              data-testid="modal-save-button"
              className="flex-1"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.95) 0%, rgba(0, 200, 165, 1) 100%)',
                color: '#000',
                borderRadius: '12px',
                padding: '12px',
                fontWeight: '600',
              }}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Check className="w-5 h-5" />
                  {type === 'new-admin' ? 'Create Admin' : 'Save'}
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;
